import React, { useState } from "react";
import { homepageRoute } from "@/routing";
import { CargoMindsSmallLogo, Spinner } from "@/components/common/svgs";
import { Link } from "react-router";
import { TranslatedText } from "@/components/common/text";
import { TextInput } from "@/components/common/inputs";
import { Button } from "@/components/common/buttons";
import { ErrorText } from "@/components/common/errors";
import isEmail from "validator/es/lib/isEmail";
import { cn } from "@/utils/css";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { resetPassword } from "@/auth/firebase";

export const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const isDarkTheme = useIsDarkTheme();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!isEmail(email)) {
            setError("Invalid email");
            return;
        }

        setLoading(true);
        try {
            await resetPassword(email);
            setSuccess(true);
        } catch (err) {
            // Silently show success for user-not-found to prevent email enumeration
            if (err.code === "auth/user-not-found") {
                setSuccess(true);
            } else {
                setError("Failed to send reset email. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex w-full items-center justify-center min-h-[calc(100vh-2rem)] flex-col px-6">
            <div className="bg-cm-p-surface w-full sm:max-w-[476px] rounded-lg text-cm-p-text-primary flex flex-col items-center justify-center">
                <div
                    className={cn("w-full pb-10 px-6 py-8 sm:px-12 sm:py-12 bg-cm-p-background text-cm-p-text-primary rounded-lg", {
                        "shadow-login-light": !isDarkTheme,
                    })}
                >
                    <div className="w-full flex flex-col items-center justify-center">
                        <Link to={homepageRoute} className="h-9 w-[80px] mb-5">
                            <CargoMindsSmallLogo />
                        </Link>
                        <div className="text-[32px] leading-9 text-cm-p-text-primary font-medium mb-14">
                            <TranslatedText>Reset Password</TranslatedText>
                        </div>
                    </div>
                    {success ? (
                        <div className="flex flex-col items-center gap-6">
                            <p className="text-cm-p-text-secondary text-center">
                                <TranslatedText>
                                    If an account exists with this email, we've sent a password reset link. Please check your inbox.
                                </TranslatedText>
                            </p>
                            <Link to="/login" className="text-cm-p-primary hover:underline">
                                <TranslatedText>Back to Login</TranslatedText>
                            </Link>
                        </div>
                    ) : (
                        <form className="gap-6 flex flex-col mb-6 md:max-w-[380px]" onSubmit={handleSubmit}>
                            <TextInput
                                name="email"
                                type="email"
                                placeholder="Email"
                                value={email}
                                setValue={setEmail}
                                autoComplete="email"
                                autoFocus
                            />
                            <ErrorText text={error} />
                            <div className="h12">
                                <Button disabled={loading} type="submit">
                                    {loading ? <Spinner className="w-5 h-5" /> : <TranslatedText>Send Reset Link</TranslatedText>}
                                </Button>
                            </div>
                            <div className="text-center">
                                <Link to="/login" className="text-cm-p-primary hover:underline">
                                    <TranslatedText>Back to Login</TranslatedText>
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
